import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findParentNode } from '@atlaskit/editor-prosemirror/utils';
import { fg } from '@atlaskit/platform-feature-flags';

import { getSupportedNodesForAIModal } from '../../ui/utils/ai-button';

import { aiButtonPluginKey } from './ai-button-plugin-key';

export type AIButtonPluginState = {
	areAiEntryPointsDisabled?: boolean;
	isSelectionValidForAIExperience: boolean;
	triggeredFor?: NodeType;
};

export const createAIButtonPlugin = () => {
	return new SafePlugin<AIButtonPluginState>({
		key: aiButtonPluginKey,

		state: {
			init: () => ({ isSelectionValidForAIExperience: true }),

			// eslint-disable-next-line @typescript-eslint/max-params
			apply: (tr, _pluginState, _oldEditorState, newEditorState) => {
				if (fg('platform_editor_ai_toggle_entry_points')) {
					const toggleAiEntryPoints = tr.getMeta('toggleAiEntryPoints');
					if (typeof toggleAiEntryPoints === 'boolean') {
						return {
							..._pluginState,
							areAiEntryPointsDisabled: toggleAiEntryPoints,
						};
					}
				}

				if (tr.selectionSet) {
					const selection = tr.selection;
					const { $from } = selection;
					const parent = $from.parent;
					const { codeBlock, tableHeader, tableCell, tabeRow, table } = newEditorState.schema.nodes;
					const isNodeSelection = selection instanceof NodeSelection;
					const isSelectionInCodeBlock = parent.type === codeBlock;
					const isCellSelection = [tableHeader, tableCell, tabeRow, table].includes(parent.type);

					// AI button is always disabled for Node selection.
					// AI button is always disabled for Cell selection.
					// AI button is always disabled for selection in code block.
					// For all other selection and empty selections AI button is enabled.
					let isSelectionValidForAIExperience = true;
					if (isSelectionInCodeBlock || isNodeSelection || isCellSelection) {
						isSelectionValidForAIExperience = false;
					}

					let newPluginState: AIButtonPluginState = {
						areAiEntryPointsDisabled: _pluginState?.areAiEntryPointsDisabled,
						isSelectionValidForAIExperience,
					};

					const getParentSupportedForAIButton = () => {
						const supportedNodesForAI = getSupportedNodesForAIModal(newEditorState);

						if (isNodeSelection) {
							return supportedNodesForAI.has(selection.node.type) ? selection.node : undefined;
						} else {
							const parentNodeFindResult = findParentNode((node) =>
								supportedNodesForAI.has(node.type),
							)(selection);
							return parentNodeFindResult?.node;
						}
					};
					const parentSupportedForAIButton = getParentSupportedForAIButton();
					const { from, to } = selection;
					if ((from === to || isNodeSelection || isCellSelection) && parentSupportedForAIButton) {
						const isTextSelection = selection instanceof TextSelection;
						const isSelectionValidForAIExperience =
							!isSelectionInCodeBlock && (isTextSelection || !!parentSupportedForAIButton);
						const triggeredFor = parentSupportedForAIButton?.type;

						newPluginState = {
							areAiEntryPointsDisabled: _pluginState?.areAiEntryPointsDisabled,
							isSelectionValidForAIExperience,
							triggeredFor,
						};
					}

					return newPluginState;
				}

				return _pluginState;
			},
		},
	});
};
